import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import type { IViewChoicesPreferences } from '@featit/shared';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';
import { expectRecord, UnreadableAnswerError } from '@/services/answerShape';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import { SESSION_END_CAUSES, type SessionEndCauseType } from '@/services/ApiService';
import { VIEW_CHOICES_STORAGE_PREFIX } from '@/features/views/viewChoicesKeys';
import type {
  IViewChoicesApi,
  IViewChoicesSyncOptions,
  ViewChoicesSync,
} from '@/features/views/viewChoicesSync';

/**
 * Builds the sync. Handed in by the view that asks (`useViewChoicesSync`), never imported here:
 * this store is created at boot, and `ViewChoicesSync` is code only a view needs. Imported here it
 * sat in the launch tab's entry chunk; handed in, it arrives with the first view's own chunk (T-0559
 * entry budget, gate 474).
 */
export type ViewChoicesSyncFactoryType = (options: IViewChoicesSyncOptions) => ViewChoicesSync;

/**
 * F-0527 (ADR-165): the person's view choices as the app holds them — whether they are kept across
 * devices (the setting, default on) and the one {@link ViewChoicesSync} for the signed-in person.
 *
 * ⛔ **Nothing is sent until the setting is known.** A person who turned it off must not have a choice
 * leave the device in the moment before the preference arrives, so `syncFor` answers `null` — the old
 * session-only behaviour — until the preference has been read, and again whenever it is off. It also
 * answers `null` with no signed-in person, which is every unit test of a view.
 *
 * ⛔ **Nothing is sent without the person's session (I-1311).** A sync is one person's, for one
 * session. It is told whether that session is still there (the stored tokens, and the signed-in
 * person still being the one it was made for) and ends itself when it is not; `forget` ends it when
 * the store is told. A sync that has ended is never handed out again.
 *
 * **A choice not sent yet is the person's own unsent work (I-1311, F-0534 PPD-2).** It stays in
 * storage across their own expiry or refused refresh, and their next session's sync sends it. A
 * sign-out drops what is left (`sessionEnded`); another person signing in drops it by the owner
 * rule.
 */
export interface IViewChoicesStoreApi extends IViewChoicesApi {
  preferences(): Promise<IViewChoicesPreferences>;
  updatePreferences(keepAcrossDevices: boolean): Promise<IViewChoicesPreferences>;
}

export class ViewChoicesStore {
  /** `null` until read; then the person's setting. */
  keepAcrossDevices: boolean | null = null;
  /** Why the setting could not be read or saved, for the Settings screen. */
  heldError: HeldSentenceType | null = null;
  get error(): string | null {
    return sayFailure(pageWords(), this.heldError);
  }
  set error(value: HeldSentenceType | null) {
    this.heldError = value;
  }
  /** The failed read of the setting, for its Try again (T-0556); `null` once it has been read. */
  loadFailure: ILoadFailure | null = null;
  loading = false;
  saving = false;
  private sync: ViewChoicesSync | null = null;
  private syncUserId: string | null = null;
  private loadedFor: string | null = null;

  constructor(
    private readonly options: {
      userId: () => string | null;
      api: IViewChoicesStoreApi;
      storage: () => Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'> | null;
      /** I-1311: whether this browser holds a session now, whoever's it is. */
      hasSession: () => boolean;
    },
  ) {
    makeObservable(this, {
      keepAcrossDevices: observable,
      heldError: observable.ref,
      error: computed,
      loadFailure: observable,
      loading: observable,
      saving: observable,
      load: action,
      setKeepAcrossDevices: action,
    });
  }

  /**
   * The setting for the signed-in person, once per person. Failure leaves it unknown (session only),
   * and a call after a failure reads it again (the Settings screen's Try again). An answer that is not
   * the setting — a list, an empty record — is a failed read like a refused one (I-1162).
   */
  async load(): Promise<void> {
    const userId = this.options.userId();
    if (!userId || this.loadedFor === userId) return;
    this.loadedFor = userId;
    this.loading = true;
    try {
      const preferences = expectRecord<IViewChoicesPreferences>(
        await this.options.api.preferences(),
        'Your view-choice setting',
        ['keepAcrossDevices'],
      );
      if (typeof preferences.keepAcrossDevices !== 'boolean') {
        throw new UnreadableAnswerError('Your view-choice setting');
      }
      // I-1311: an answer for a person who has gone since is nobody's setting here.
      if (this.loadedFor !== userId || this.options.userId() !== userId) return;
      runInAction(() => {
        this.keepAcrossDevices = preferences.keepAcrossDevices;
        this.error = null;
        this.loadFailure = null;
      });
    } catch (error) {
      if (this.loadedFor !== userId || this.options.userId() !== userId) return;
      this.loadedFor = null;
      runInAction(() => {
        this.error = failureToSay(error, 'views.actions.loadChoices');
        this.loadFailure = describeLoadFailure(error);
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async setKeepAcrossDevices(value: boolean): Promise<void> {
    this.saving = true;
    this.error = null;
    try {
      const saved = await this.options.api.updatePreferences(value);
      runInAction(() => {
        this.keepAcrossDevices = saved.keepAcrossDevices;
      });
    } catch (error) {
      runInAction(() => {
        this.error = failureToSay(error, 'views.actions.saveSetting');
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** The sync to use now, or `null` for session-only choices (see the class note). */
  syncFor(create: ViewChoicesSyncFactoryType): ViewChoicesSync | null {
    const userId = this.options.userId();
    if (!userId) {
      this.sync = null;
      this.syncUserId = null;
      return null;
    }
    void this.load();
    if (this.keepAcrossDevices !== true) return null;
    if (this.sync && this.syncUserId === userId && !this.sync.hasEnded) return this.sync;
    // I-1311: another person's sync, or one whose session went: ended, never carried over.
    this.sync?.forgetEverything();
    this.sync = create({
      api: this.options.api,
      storage: this.options.storage(),
      userId,
      hasSession: () => this.options.hasSession() && this.options.userId() === userId,
    });
    this.syncUserId = userId;
    return this.sync;
  }

  /**
   * I-1287: the person is signing out. What their views still have waiting is sent now, while the
   * session that may write it is still there; `forget` follows once it is gone.
   */
  sendBeforeSignOut(): void {
    this.sync?.leavePage();
  }

  /**
   * The signed-in person changed, or their session ended (I-1311): their sync is ended, and the
   * next person on this browser starts from nothing, their setting unknown until it is read. What
   * the sync had written to storage stays (see `sessionEnded`).
   */
  forget(): void {
    this.sync?.forgetEverything();
    this.sync = null;
    this.syncUserId = null;
    this.loadedFor = null;
    this.keepAcrossDevices = null;
  }

  /**
   * A session ended, for `cause` (`endSession`). The sync ends for every cause. At a sign-out the
   * person's write has gone already (`sendBeforeSignOut`), and what is left of their choices on
   * this device is dropped, whether or not a view had made a sync. For any other cause the stored
   * choices wait for their person.
   */
  sessionEnded(cause: SessionEndCauseType): void {
    this.forget();
    if (cause === SESSION_END_CAUSES.SIGN_OUT) this.dropStoredChoices();
  }

  private dropStoredChoices(): void {
    try {
      const storage = this.options.storage();
      if (!storage) return;
      const keys: string[] = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith(VIEW_CHOICES_STORAGE_PREFIX)) keys.push(key);
      }
      for (const key of keys) storage.removeItem(key);
    } catch {
      // A storage that cannot be read holds nothing to drop.
    }
  }
}
